import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import Case from 'case';
import uniqid from 'uniqid';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  AppButton,
  ProductRowStats,
  UnderConstruction,
  SubmissionLoader,
  FlexibleSelectDropdown,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { ReviewTablePartial } from '@Components/Partial/Product/Dashboard';
import {
  initializeReviewedRows,
  updateRowReviewStatus,
  updateMultipleRowsReviewStatus,
} from '@Helpers/controlFunctions';
import { getSelectedMonetizationResultIds } from '@Helpers/resultsMonetization';
import {
  initiateProductReviewRequest,
  clearProductReviewRequest,
  initiateProductReviewStatsRequest,
  clearProductReviewStatsRequest,
  initiateProductReviewMetaRequest,
  clearProductReviewMetaRequest,
  updateProductReviewSelectionsRequest,
  bulkUpdateReviewStatusByDomainRequest,
  initiateResultsMonetizationRequest,
  initiateLiveTrackerAnalyticsRequest,
  clearLiveTrackerAnalyticsRequest,
} from '@Actions';
import {
  ReviewFilterDropdown, SortDropdown,
} from '../../../Partial/Product/SharedComponents';

const ReviewPage = ({
  small,
  size,
  location,
  loading,
  submitLoading,
  fetchReviewData,
  clearReviewDashboard,
  fetchReviewStats,
  clearReviewStats,
  fetchReviewMeta,
  clearReviewMeta,
  updateProductReviewSelections,
  bulkUpdateReviewStatusByDomain,
  reviewStats = null,
  reviewMeta = null,
  reviewData = null,
  reviewDisabled = false,
  paymentPending = false,
  contentIdEnabled = false,
  monitoringEnabled = false,
  createResultsMonetizationRequest,
  hostingData = null,
  fetchHostingData,
  clearHostingData,
}) => {
  const [reviewedData, setReviewedData] = React.useState([]);
  const [selectedRowIds, setSelectedRowIds] = React.useState([]);
  const [resultsPerPage, setResultsPerPage] = React.useState(10);
  const [activeReviewTab, setActiveReviewTab] = React.useState(null);
  /* eslint-disable camelcase */
  const [query, setQuery] = React.useState({
    filter: {
      domain: null,
      platforms: null,
    },
    sort: {
      created_at: null,
    },
    filterResetKey: uniqid(),
    sortResetKey: uniqid(),
  });
  const {
    theme: { page, button, navigation },
    disableReviewFeatures,
    constructionImage,
    isDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab });
    fetchReviewStats();
    fetchReviewMeta();
    fetchHostingData();

    return () => {
      clearReviewDashboard();
      clearReviewStats();
      clearReviewMeta();
      clearHostingData();
    };
  }, []);

  React.useEffect(() => {
    const queryObj = {
      limit: resultsPerPage,
      tab_id: activeReviewTab,
    };

    if (query.filter.domain || query.filter.platforms) {
      queryObj.query_filters = query.filter;
    }

    if (query.sort.created_at) {
      queryObj.query_sorts = query.sort;
    }

    fetchReviewData(queryObj);
  }, [query]);

  const handleResultsPerPage = (val) => {
    setResultsPerPage(val);
    fetchReviewData({ limit: val, tab_id: activeReviewTab });
  };

  const handleActiveTab = (val) => {
    setActiveReviewTab(val);
    fetchReviewData({ limit: resultsPerPage, tab_id: val });
  };

  const handleTabFilters = (key, val, reset = false) => {
    if (reset) {
      setQuery((prev) => ({ sort: { ...prev.sort }, filter: { domain: null, platforms: null } }));
      fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab });
      return;
    }

    setQuery((prev) => ({ sort: { ...prev.sort }, filter: { ...prev.filter, [key]: val } }));
  };

  const handleTabSort = (key, val) => {
    if (!key) {
      setQuery((prev) => ({ filter: { ...prev.filter }, sort: { created_at: null } }));
      fetchReviewData({
        limit: resultsPerPage, tab_id: activeReviewTab, query_filters: query.filter,
      });
      return;
    }

    setQuery((prev) => ({ filter: { ...prev.filter }, sort: { ...prev.sort, [key]: val } }));
  };

  const handleResetQuery = () => {
    setQuery({
      filter: {
        domain: null,
        platforms: null,
      },
      sort: {
        created_at: null,
      },
      filterResetKey: uniqid(),
      sortResetKey: uniqid(),
    });
  };

  const submitDisabled = reviewedData.some(({ review_status }) => review_status === 'pending');

  const sendReviewedResults = () => {
    const filteredSelections = _.filter(
      reviewedData,
      ({ review_status, prev_status }) => review_status !== 'pending' && review_status !== prev_status,
    ).map(({ id, review_status, prev_status }) => ({
      id,
      review_status,
      prev_status,
    }));

    updateProductReviewSelections({ data: filteredSelections }, () => {
      fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab });
      fetchReviewStats();
    });
    clearReviewDashboard();
    clearReviewStats();
    setReviewedData([]);
    window.scrollTo(0, 0);
  };

  const handleBulkUpdateReviewStatusByDomain = (bulkQuery) => {
    bulkUpdateReviewStatusByDomain(bulkQuery, () => {
      clearReviewDashboard();
      clearReviewStats();
      setReviewedData([]);
      window.scrollTo(0, 0);
      fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab });
      fetchReviewStats();
    });
  };

  const handleExportCSV = () => {
    fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab }, true);
  };

  const tableData = reviewData?.data && reviewData.data[0];
  const reviewDataHeaders = small ? _.filter(tableData?.value?.header, ((dt) => dt.display !== 'Review Status')) : tableData?.value?.header;
  const rawReviewRows = tableData?.value?.rows || [];
  const monetizationSelection = getSelectedMonetizationResultIds({
    headers: tableData?.value?.header,
    rows: rawReviewRows.map((row) => (Array.isArray(row) ? row : (row.cells || row))),
    selectedRowIds,
  });
  const resultsPerPageOptions = [10, 15, 20, 25, 50];
  const currentTab = reviewMeta?.tabs?.find((tab) => tab.id === activeReviewTab);
  const currentFilters = currentTab?.queryFilters;
  const currentSorts = currentTab?.querySorts;
  const selectedTabDisplay = currentTab?.name || null;
  const withFilters = query.filter.domain || query.filter.platforms;
  const underMaintenance = (disableReviewFeatures || reviewDisabled);
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const canRequestMonetization = Boolean(contentIdEnabled && monitoringEnabled);
  const monetizationDisabled = !monetizationSelection.allSelectedEligible;
  const monetizationTitle = !monetizationSelection.hasSelection
    ? 'Select one or more YouTube results'
    : 'Select only YouTube results';

  const padding = small ? '1.5rem' : '2rem';

  const requestMonetization = () => {
    if (!monetizationSelection.allSelectedEligible) return;

    createResultsMonetizationRequest(
      {
        // eslint-disable-next-line camelcase
        result_ids: monetizationSelection.eligibleIds,
      },
      () => {
        clearReviewDashboard();
        clearReviewStats();
        setReviewedData([]);
        setSelectedRowIds([]);
        fetchReviewData({ limit: resultsPerPage, tab_id: activeReviewTab });
        fetchReviewStats();
        window.scrollTo(0, 0);
      },
    );
  };

  const renderPageContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <UnderConstruction
            imageUrl={constructionImage}
            buttonHighlight={page.highlight}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    return (
      <Box flex background={usePageBg} pad={reviewStats ? '0rem' : { top: padding }}>
        {reviewStats && (
          <Box width="100%" direction="row" justify="end" pad={{ vertical: '0.65rem' }}>
            <ProductRowStats small={small} label="Positives:" stat={reviewStats.positives} color={page.primaryText} />
            <ProductRowStats small={small} label="Reviewed:" stat={reviewStats.reviewed} color={page.primaryText} />
            <ProductRowStats isLast small={small} label="Pending:" stat={reviewStats.pendingForCompany} color={page.primaryText} />
          </Box>
        )}
        <ReviewTablePartial
          loading={loading}
          reviewData={rawReviewRows}
          reviewDataHeaders={reviewDataHeaders}
          reviewStatusOptions={tableData?.statusOptions}
          initializeReviewedRows={(data) => (
            initializeReviewedRows(setReviewedData, data)
          )}
          updateRowReviewStatus={(editableId, selectedOption) => (
            updateRowReviewStatus(reviewedData, setReviewedData, editableId, selectedOption)
          )}
          updateMultipleRowsReviewStatus={(updateableRows, selectedOption) => (
            updateMultipleRowsReviewStatus(
              reviewedData, setReviewedData, updateableRows, selectedOption,
            )
          )}
          bulkUpdateReviewStatusByDomain={(bulkQuery) => {
            handleBulkUpdateReviewStatusByDomain(bulkQuery);
          }}
          selectedStatuses={reviewedData}
          handleResultsPerPage={(val) => handleResultsPerPage(val)}
          resultsPerPageOptions={resultsPerPageOptions}
          resultsPerPage={resultsPerPage}
          totalResults={reviewStats && reviewStats.pendingForCompany}
          handleExportCSV={handleExportCSV}
          size={size}
          paymentPending={paymentPending}
          onSelectionChange={setSelectedRowIds}
          hostingData={hostingData?.data}
          includeISPData
          tableControls={reviewMeta?.tabs?.length > 0 && (
            <Box direction="row" gap="1rem">
              <FlexibleSelectDropdown
                withReset
                buttonWidth="9rem"
                buttonJustify="between"
                flexibleOptions={reviewMeta.tabs}
                selectedOption={selectedTabDisplay}
                handleItemSelect={(val) => handleActiveTab(val)}
                textColor={page.inputText}
                displayKey="name"
                valueKey="id"
                noSelectionLabel="Select filter group"
              />
              {currentFilters?.length > 0 && (
                <ReviewFilterDropdown
                  key={query.filterResetKey}
                  small={small}
                  selectedFilters={withFilters ? query.filter : null}
                  handleFilter={(uid, value, reset) => (
                    handleTabFilters(uid, value, reset)
                  )}
                  filterOptions={currentFilters}
                  optionNames={currentFilters.map(({ name }) => (
                    { name, formatted: Case.capital(name) }
                  ))}
                />
              )}
              {currentSorts?.options?.length > 0 && (
                <SortDropdown
                  key={query.sortResetKey}
                  selectedSortOption={query.sort.created_at ? { name: 'created_at', direction: query.sort.created_at } : null}
                  handleSort={(sortValue, sortDirection) => handleTabSort(sortValue, sortDirection)}
                  sortOptions={currentSorts?.options}
                  orderOptions={currentSorts?.directions}
                />
              )}
            </Box>
          )}
          showRefresh={query.filter.domain || query.filter.platforms || query.sort.created_at}
          handleRefresh={() => handleResetQuery()}
        />
        {tableData?.value?.rows?.length >= 1 && (
          <Box
            width="100%"
            direction={small ? 'column' : 'row'}
            gap={small ? '1rem' : '2rem'}
            justify="between"
            align="center"
            pad={{ horizontal: padding, vertical: '2rem' }}
          >
            <Box width={small ? '100%' : '65%'}>
              <Text size="1rem" weight={500} color={page.primaryText}>
                We will automatically send a DMCA notice for results marked as
                infringements when you&apos;re ready for the next set of results.
              </Text>
            </Box>
            <Box pad={{ right: small ? '0rem' : '4rem' }}>
              <Box direction={small ? 'column' : 'row'} gap="0.75rem">
                {canRequestMonetization && (
                  <AppButton
                    overrideHover
                    width="12rem"
                    disabled={monetizationDisabled}
                    onClick={requestMonetization}
                    level="dynamicLarge"
                    color={button.primaryText}
                    bgColor={button.secondaryBg || button.primaryBg}
                    fontWeight={600}
                    label="Request Monetization"
                    title={monetizationDisabled ? monetizationTitle : null}
                  />
                )}
                <AppButton
                  overrideHover
                  width="10rem"
                  disabled={submitDisabled || (reviewedData.length === 0)}
                  onClick={() => sendReviewedResults(reviewedData)}
                  level="dynamicLarge"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Review Next Set"
                  title={submitDisabled ? 'Please select a status option for all rows' : null}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  /* eslint-enable camelcase */

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Review infringements',
        bannerChildren: (
          <Box>
            <Text color={navigation.bannerTextColor} size="1rem">
              Select which enforcement action, if any, should be taken for each infringement.
            </Text>
          </Box>
        ),
        bannerChildrenPosition: 'bottom',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Processing your request..."
          contentWidth="20rem"
        />
      )}
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    reviewData: state.productReview.productReviewData,
    reviewStats: state.productReview.productReviewStats,
    reviewMeta: state.productReview.productReviewMeta,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    hostingData: state.analytics.liveTrackerData,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchReviewData: initiateProductReviewRequest,
    clearReviewDashboard: clearProductReviewRequest,
    fetchReviewStats: initiateProductReviewStatsRequest,
    clearReviewStats: clearProductReviewStatsRequest,
    fetchReviewMeta: initiateProductReviewMetaRequest,
    clearReviewMeta: clearProductReviewMetaRequest,
    updateProductReviewSelections: updateProductReviewSelectionsRequest,
    bulkUpdateReviewStatusByDomain: bulkUpdateReviewStatusByDomainRequest,
    createResultsMonetizationRequest: initiateResultsMonetizationRequest,
    fetchHostingData: initiateLiveTrackerAnalyticsRequest,
    clearHostingData: clearLiveTrackerAnalyticsRequest,
  }, dispatch);
}

ReviewPage.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  updateProductReviewSelections: PropTypes.func.isRequired,
  bulkUpdateReviewStatusByDomain: PropTypes.func.isRequired,
  fetchReviewData: PropTypes.func.isRequired,
  clearReviewDashboard: PropTypes.func.isRequired,
  fetchReviewStats: PropTypes.func.isRequired,
  clearReviewStats: PropTypes.func.isRequired,
  fetchReviewMeta: PropTypes.func.isRequired,
  clearReviewMeta: PropTypes.func.isRequired,
  reviewData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      feature: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string.isRequired,
      statusOptions: PropTypes.arrayOf(PropTypes.shape({
        api: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      })).isRequired,
      value: PropTypes.shape({
        header: PropTypes.arrayOf(PropTypes.shape({
          display: PropTypes.string.isRequired,
        }).isRequired).isRequired,
        rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
          api: PropTypes.string,
          decoration: PropTypes.any,
          display: PropTypes.any,
          editable: PropTypes.string,
          editableId: PropTypes.string,
          editableOptions: PropTypes.arrayOf(PropTypes.shape({
            api: PropTypes.string.isRequired,
            display: PropTypes.string.isRequired,
          })),
          unit: PropTypes.string,
        }).isRequired).isRequired).isRequired,
      }).isRequired,
    }).isRequired).isRequired,
  }),
  reviewStats: PropTypes.shape({
    positives: PropTypes.number,
    reviewed: PropTypes.number,
    pendingForCompany: PropTypes.number,
  }),
  reviewMeta: PropTypes.shape({
    tabs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      queryFilters: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.shape({
          key: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }).isRequired),
      }).isRequired).isRequired,
      querySorts: PropTypes.shape({
        directions: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired),
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired),
      }).isRequired,
    }).isRequired).isRequired,
  }),
  reviewDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
  contentIdEnabled: PropTypes.bool,
  monitoringEnabled: PropTypes.bool,
  createResultsMonetizationRequest: PropTypes.func.isRequired,
  hostingData: PropTypes.shape({ data: PropTypes.array }),
  fetchHostingData: PropTypes.func.isRequired,
  clearHostingData: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ReviewPage));
